// Client API admin: dashboard consumi (Sprint 4).

import { z } from 'zod';
import { request } from './_request';

const VoiceProviderBreakdownSchema = z.record(
  z.string(),
  z.object({
    seconds: z.number().optional(),
    chars: z.number().optional(),
    cost_usd: z.number(),
  }),
);

const UsageMonthlySchema = z.object({
  year_month: z.string(),
  total_encounters: z.number(),
  total_completed: z.number(),
  total_abandoned: z.number(),
  total_emergency: z.number(),
  total_input_tokens: z.number(),
  total_output_tokens: z.number(),
  total_cached_tokens: z.number(),
  total_cache_creation_tokens: z.number(),
  estimated_cost_usd: z.number(),
  by_model: z.record(z.string(), z.object({ tokens: z.number(), count: z.number() })),
  by_language: z.record(z.string(), z.number()),
  by_flow: z.record(z.string(), z.number()),
  // Sprint 8 — audio metrics (default 0 per backwards compat)
  total_audio_seconds_user: z.number().default(0),
  total_audio_chars_bot: z.number().default(0),
  estimated_cost_audio_usd: z.number().default(0),
  by_voice_provider: VoiceProviderBreakdownSchema.default({}),
  soft_limit_alert_sent_at: z.string().nullable(),
  hard_limit_reached_at: z.string().nullable(),
  updated_at: z.string(),
});

export type UsageMonthly = z.infer<typeof UsageMonthlySchema>;

const UsageDashboardSchema = z.object({
  ok: z.literal(true),
  year_month: z.string(),
  current: UsageMonthlySchema.nullable(),
  previous: UsageMonthlySchema.nullable(),
  daily_encounters: z.array(
    z.object({
      day: z.string(),
      total: z.number(),
      cost_usd: z.number(),
    }),
  ),
  by_model: z.record(z.string(), z.object({ tokens: z.number(), count: z.number() })),
  by_language: z.record(z.string(), z.number()),
  by_flow: z.array(z.object({ flow_id: z.string(), name: z.string(), count: z.number() })),
  limits: z.object({
    monthly_soft: z.number(),
    monthly_hard: z.number(),
  }),
  alert_email_recipient: z.string().nullable(),
  recent_encounters: z.array(
    z.object({
      id: z.string().uuid(),
      pseudonym: z.string(),
      status: z.string(),
      language_detected: z.string().nullable(),
      total_turns: z.number(),
      started_at: z.string(),
      transport_mode: z.enum(['text', 'voice', 'mixed']).default('text'),
    }),
  ),
  encounters_by_transport: z
    .object({
      text: z.number().default(0),
      voice: z.number().default(0),
      mixed: z.number().default(0),
    })
    .default({ text: 0, voice: 0, mixed: 0 }),
});

export type UsageDashboardResponse = z.infer<typeof UsageDashboardSchema>;

export async function getUsageDashboard(yearMonth?: string): Promise<UsageDashboardResponse> {
  const qs = yearMonth ? `?year_month=${encodeURIComponent(yearMonth)}` : '';
  const json = await request<unknown>(`/api/admin/usage${qs}`);
  return UsageDashboardSchema.parse(json);
}

export async function updateLimits(input: {
  monthly_soft: number;
  monthly_hard: number;
  alert_email_recipient?: string | null;
}): Promise<{ ok: true }> {
  const json = await request<unknown>('/api/admin/usage/limits', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return z.object({ ok: z.literal(true) }).parse(json);
}

export async function triggerCron(job: 'idleTimeout' | 'usageAggregation'): Promise<unknown> {
  const json = await request<unknown>(`/api/admin/cron/trigger/${encodeURIComponent(job)}`, {
    method: 'POST',
  });
  return json;
}
